import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  getLivePrice, getTradeLock, getRowImage, getStickerPct, describeStickerPct,
  buildStickerIndex, withStickerImages,
} from './helpers';
import { TradePartnerBadge } from './TradePartner';
import ItemDetailModal, { DetailButton, TradeButton, hasTrade } from './ItemDetailModal';
import useGroupPick from './useGroupPick';
import StickerSlots from './StickerSlots';
import { isFlipRow } from './investments';
import { isLent } from './lending';
import { useSettling, settlesInLabel } from './settlingSales';

// Live 2 — "Armory": the holdings redesigned as a showcase card grid.
// Same data as the Live tab; clicking a card opens the standard editor.
const COND_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field Tested': 'FT',
  'Well Worn': 'WW', 'Battle Scarred': 'BS',
};

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

const hexToRgba = (hex, a) => {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(x => x + x).join('');
  if (h.length !== 6) return `rgba(129,140,248,${a})`;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const SORTS = [
  { key: 'value',  label: 'Highest cost' },
  { key: 'delta',  label: 'Biggest gain' },
  { key: 'deltaAsc', label: 'Biggest loss' },
  { key: 'newest', label: 'Newest first' },
  { key: 'name',   label: 'Name A–Z' },
];

export default function LiveTab2({
  rows, settings, livePrices, csfloatPrices, skinsData = [], inventoryMap, onEdit,
  onBulkAdd, tradePartners, onGroupPrice, onSetCategory,
  lendingEnabled, onLendItems, onMoveToInvestment,
}) {
  const [query, setQuery] = useState('');
  const [cat, setCat]     = useState('all');
  const [sort, setSort]   = useState('value');
  const [detail, setDetail] = useState(null);   // the item whose modal is open
  // Ticking cards feeds three different actions, so one picker is shared and
  // this decides what the bar at the bottom offers. Switching mode drops the
  // selection: a set picked to be priced together is rarely the set you want to
  // tag or send somewhere else.
  const [mode, setMode] = useState(null);       // 'price' | 'move' | 'type' | null

  const settlingOf = useSettling();

  const holdings = useMemo(() => {
    return (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      // Investments live in the Vault only — one item, one home.
      .filter(({ row }) => isFlipRow(row))
      .map(e => {
        const buy  = parseFloat(e.row.buyPrice) || 0;
        const lp   = getLivePrice(livePrices, e.row);
        const mkt  = lp?.starting_at?.price ?? null;
        const diff = mkt != null ? mkt - buy : null;
        const type = getItemTypes(e.row)
          .map(t => settings?.itemTypes?.find(it => it.value === t.value))
          .find(Boolean);
        const inv    = e.row.assetId ? inventoryMap?.get(e.row.assetId) : null;
        // Sold on CSFloat and waiting out trade protection. The row stays here
        // until the sale is verified (see settlingSales.js), but it is no longer
        // an item you are waiting to be able to sell, so it is not "locked".
        const settling = settlingOf(e.row);
        const lock = getTradeLock(e.row, inv);
        const locked = settling ? false : lock.locked;
        const until  = lock.until;
        // Crafts only: what percentage of the sticker value the price paid
        // covered, once the bare skin is taken out of it.
        const sp = getStickerPct(e.row, livePrices);
        return { ...e, buy, mkt, diff, type, locked, until, settling, sp };
      });
  }, [rows, livePrices, settings, inventoryMap, settlingOf]);

  const cats = useMemo(() => {
    const present = new Set();
    holdings.forEach(h => getItemTypes(h.row).forEach(t => present.add(t.value)));
    return (settings?.itemTypes || []).filter(t => present.has(t.value));
  }, [holdings, settings]);

  // Rows built from the trade history carry sticker names but no pictures, so
  // the strip would draw five empty numbered boxes. Built once: the database is
  // 40k entries and this would otherwise be walked per sticker, per card.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = holdings.filter(h => {
      if (q && !h.row.skin.value.toLowerCase().includes(q)) return false;
      if (cat !== 'all' && !getItemTypes(h.row).some(t => t.value === cat)) return false;
      return true;
    });
    const by = {
      value:    (a, b) => b.buy - a.buy,
      delta:    (a, b) => (b.diff ?? -Infinity) - (a.diff ?? -Infinity),
      deltaAsc: (a, b) => (a.diff ?? Infinity) - (b.diff ?? Infinity),
      newest:   (a, b) => new Date(b.row.timestamp) - new Date(a.row.timestamp),
      name:     (a, b) => a.row.skin.value.localeCompare(b.row.skin.value),
    }[sort];
    return [...list].sort(by);
  }, [holdings, query, cat, sort]);

  // Items out on loan left this page but not your portfolio — the Overview still
  // counts them. Said here because otherwise lending a knife and losing one look
  // identical from the Armory: the count and the cost both just drop.
  const lentOut = useMemo(() => {
    const out = (rows || []).filter(r => isLent(r) && r.skin?.value);
    return { count: out.length, cost: out.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0) };
  }, [rows]);

  const totals = useMemo(() => {
    const cost = holdings.reduce((s, h) => s + h.buy, 0);
    let value = 0, unreal = 0, priced = 0;
    holdings.forEach(h => {
      if (h.mkt != null) { value += h.mkt; unreal += h.mkt - h.buy; priced++; }
      else value += h.buy;
    });
    return { cost, value, unreal, priced, locked: holdings.filter(h => h.locked).length };
  }, [holdings]);

  const roi = totals.cost > 0 ? totals.unreal / totals.cost * 100 : null;

  // Group pricing: tick several cards, then split one amount across them.
  // The same picker drives Move, see `mode` above.
  const gp = useGroupPick(filtered, holdings);
  const pick = m => { const next = mode === m ? null : m; setMode(next); gp.setOn(!!next); };

  // A row can only go somewhere it is not already. Counting the ones that
  // cannot move keeps the bar honest about what a click will actually do.
  // Investments no longer reach this list at all, so every pick can move to the
  // Vault — only the lend count still has anything to exclude.
  const movable = useMemo(() => {
    const e = gp.picked.entries;
    return {
      lend:   e.filter(x => !x.row.lend).length,
      invest: gp.picked.count,
    };
  }, [gp.picked]);

  // How many of the picked items already carry each category. This is what
  // makes one pill do both jobs: a category everything already has is taken
  // off, anything else is put on, so a half-tagged selection is levelled up
  // rather than flipped item by item.
  const tagged = useMemo(() => {
    const counts = {};
    gp.picked.entries.forEach(e =>
      getItemTypes(e.row).forEach(t => { if (t?.value) counts[t.value] = (counts[t.value] || 0) + 1; }));
    return counts;
  }, [gp.picked]);

  const applyType = t => {
    const n = tagged[t.value] || 0;
    onSetCategory(gp.picked.entries.map(e => e.realIndex), t, n === gp.picked.count ? 'remove' : 'add');
  };

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';

  return (
    <div className="lv2-root">

      {/* ── Header ─────────────────────────────────── */}
      <div className="lv2-head">
        <div className="lv2-title">
          Armory
          <small>
            {holdings.length} items currently held
            {totals.locked > 0 ? ` · ${totals.locked} trade locked` : ''}
          </small>
        </div>
        <div className="lv2-inline-stats">
          <div className="lv2-inline-stat">
            <div className="k">Cost</div>
            <div className="v">${totals.cost.toFixed(2)}</div>
            <div className="s">
              {lentOut.count > 0
                ? `cost of what's in hand · $${lentOut.cost.toFixed(2)} more is out on loan`
                : 'cost of everything held'}
            </div>
          </div>
          <div className="lv2-inline-stat">
            <div className="k">Market value</div>
            <div className="v" style={{ color: 'var(--indigo)' }}>${totals.value.toFixed(2)}</div>
            <div className="s">{totals.priced} of {holdings.length} have a live price</div>
          </div>
          <div className="lv2-inline-stat">
            <div className="k">Unrealized</div>
            <div className="v" style={{ color: totals.unreal >= 0 ? profC : lossC }}>
              {totals.unreal >= 0 ? '+' : '−'}${Math.abs(totals.unreal).toFixed(2)}
            </div>
            <div className="s">
              {roi != null
                ? `${roi >= 0 ? '+' : '−'}${Math.abs(roi).toFixed(1)}% on cost`
                : 'no live prices yet'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar">
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search your items…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <button className={`lv2-chip ${cat === 'all' ? 'on' : ''}`} onClick={() => setCat('all')}>All</button>
        {cats.map(t => (
          <button key={t.value} className={`lv2-chip ${cat === t.value ? 'on' : ''}`}
            style={cat === t.value ? { background: hexToRgba(t.color, 0.16), borderColor: hexToRgba(t.color, 0.45), color: t.color } : undefined}
            onClick={() => setCat(cat === t.value ? 'all' : t.value)}>
            {t.label}
          </button>
        ))}
        <select className="lv2-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort items">
          {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>

        {/* Item actions — the toolbar that used to live on the old Live tab */}
        <div className="lv2-actions">
          {onGroupPrice && (
            <button className={`lv2-act${mode === 'price' ? ' on' : ''}`} onClick={() => pick('price')}
              title="Select several items and split one purchase price across them">
              {mode === 'price' ? 'Cancel Select' : 'Group Price'}
            </button>
          )}
          {onSetCategory && (settings?.itemTypes || []).length > 0 && (
            <button className={`lv2-act${mode === 'type' ? ' on' : ''}`} onClick={() => pick('type')}
              title="Select several items and tag them all with a category">
              {mode === 'type' ? 'Cancel Category' : 'Set Category'}
            </button>
          )}
          {(onMoveToInvestment || (lendingEnabled && onLendItems)) && (
            <button className={`lv2-act${mode === 'move' ? ' on' : ''}`} onClick={() => pick('move')}
              title="Select items and send them to lending or investments">
              {mode === 'move' ? 'Cancel Move' : 'Move'}
            </button>
          )}
          <button className="lv2-act" onClick={onBulkAdd}>Bulk Add</button>
        </div>
      </div>

      {/* ── Grid ───────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="lv2-empty">
          {holdings.length === 0 ? (
            <>
              Nothing in the armory yet.
              <div style={{ marginTop: 14 }}>
                <button className="lv2-act primary" onClick={onBulkAdd}>Bulk add your first items</button>
              </div>
            </>
          ) : 'No items match your filters.'}
        </div>
      ) : (
        <div className="lv2-grid">
          {filtered.map((h, i) => {
            const img    = getRowImage(h.row, skinsData, inventoryMap);
            const accent = h.type?.color || '#818cf8';
            const pct    = h.diff != null && h.buy > 0 ? h.diff / h.buy * 100 : null;
            const picked = gp.sel.has(h.realIndex);
            return (
              <motion.div
                key={h.realIndex}
                className={`lv2-card${gp.on ? ' picking' : ''}${picked ? ' picked' : ''}`}
                style={{ '--lv2-accent': accent, '--lv2-glow': hexToRgba(accent, 0.13) }}
                onClick={e => gp.on
                  ? gp.click(h.realIndex, e.shiftKey)
                  : onEdit && onEdit(h.row, h.realIndex)}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 20) * 0.03 }}
              >
                <div className="lv2-stage">
                  {gp.on && <span className="lv2-pick">{picked ? '✓' : ''}</span>}
                  <DetailButton className="br" onOpen={() => setDetail({ row: h.row, img })} />
                  {/* Only on items with a trade behind them: the card click opens
                      the editor, so this is the way in to the trade overview. */}
                  {hasTrade(h.row) && (
                    <TradeButton className="br" onOpen={() => setDetail({ row: h.row, img })} />
                  )}
                  {img && <img src={img} alt="" />}
                  {h.settling ? (
                    <span className="lv2-lock selling" title={`Sold on CSFloat for $${Number(h.settling.price).toFixed(2)}, ${settlesInLabel(h.settling.settlesAt) || 'settling'}`}>
                      {h.settling.settlesAt
                        ? `Sold · ${new Date(h.settling.settlesAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : 'Sold · settling'}
                    </span>
                  ) : h.locked && (
                    <span className="lv2-lock">
                      {h.until
                        ? `🔒 ${new Date(h.until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : '🔒 Locked'}
                    </span>
                  )}
                  {h.row.float != null && <span className="lv2-float">{Number(h.row.float).toFixed(6)}</span>}
                </div>
                <div className="lv2-body">
                  <div className="lv2-name" title={h.row.skin.value}>{h.row.skin.value}</div>
                  <div className="lv2-tags">
                    {h.row.condition?.value && (
                      <span className="lv2-tag">{COND_ABBR[h.row.condition.value] || h.row.condition.value}</span>
                    )}
                    {h.type && (
                      <span className="lv2-tag" style={{ background: hexToRgba(h.type.color, 0.15), color: h.type.color }}>
                        {h.type.label}
                      </span>
                    )}
                    {/* Crafts only, and only once every price it needs is in */}
                    {h.sp && (() => {
                      const d = describeStickerPct(h.sp);
                      return <span className="lv2-tag sp" title={d.title}>{d.label}</span>;
                    })()}
                    <TradePartnerBadge row={h.row} index={tradePartners} />
                  </div>
                  {/* Same strip as Inventory. Every card renders it, empty or
                      not, so the grid stays one template: an item with no
                      stickers keeps the space rather than pulling its prices up
                      and standing out next to a craft. */}
                  <StickerSlots stickers={withStickerImages(h.row.stickers, stickerIndex)} charms={h.row.charms}
                    prices={livePrices} variant="classic"
                    hideWhenEmpty={false} blank={!h.row.condition?.value}
                    style={{ marginTop: 8, marginBottom: 0 }} />
                  <div className="lv2-prices">
                    <div className="lv2-buy">
                      <div className="k">Paid</div>
                      <div className="v">${h.buy.toFixed(2)}</div>
                    </div>
                    <div className="lv2-mkt">
                      <div className="k">Market</div>
                      <div className="v">{h.mkt != null ? `$${h.mkt.toFixed(2)}` : 'n/a'}</div>
                    </div>
                  </div>
                  <div className={`lv2-delta ${h.diff == null ? 'flat' : h.diff >= 0 ? 'up' : 'down'}`}>
                    {h.diff == null
                      ? 'no live price'
                      : `${h.diff >= 0 ? '▲ +' : '▼ −'}$${Math.abs(h.diff).toFixed(2)}${pct != null ? ` · ${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)}%` : ''}`}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Selection action bar — group price, category, or move ─ */}
      {gp.on && (
        <div className="lv-selbar">
          <span className="n">
            {gp.picked.count} selected
            {gp.picked.hidden > 0 ? ` (${gp.picked.hidden} hidden by filter)` : ''}
          </span>
          {gp.picked.count > 0 && (
            <span className="s">
              paid now <strong>${gp.picked.cost.toFixed(2)}</strong>
              {' · '}market <strong>${gp.picked.market.toFixed(2)}</strong>
              {mode === 'move'
                ? (gp.picked.count - movable.invest > 0 ? ` · ${gp.picked.count - movable.invest} already in the vault` : '')
                : mode === 'type'
                  ? ''
                  : (gp.picked.unpriced > 0 ? ` · ${gp.picked.unpriced} without a live price` : '')}
            </span>
          )}
          <span className="sep" />
          <button className="lv2-act" onClick={gp.selectAll}>Select all {filtered.length}</button>
          <button className="lv2-act" onClick={gp.clear} disabled={gp.picked.count === 0}>Clear</button>
          {mode === 'type' ? (
            <>
              {/* One pill per category. Filled means every picked item already
                  has it, so clicking takes it off; a partial count is written
                  out so it is clear the click is going to level the rest up. */}
              {(settings?.itemTypes || []).map(t => {
                const n   = tagged[t.value] || 0;
                const all = gp.picked.count > 0 && n === gp.picked.count;
                return (
                  <button key={t.value} className="lv2-act" disabled={gp.picked.count === 0}
                    style={n > 0 ? {
                      background: hexToRgba(t.color, all ? 0.20 : 0.09),
                      borderColor: hexToRgba(t.color, all ? 0.55 : 0.30),
                      color: t.color,
                    } : undefined}
                    title={all
                      ? `Remove ${t.label} from all ${gp.picked.count}`
                      : `Tag all ${gp.picked.count} as ${t.label}`}
                    onClick={() => applyType(t)}>
                    {all ? '✓ ' : ''}{t.label}
                    {n > 0 && !all ? ` (${n}/${gp.picked.count})` : ''}
                  </button>
                );
              })}
              <button className="lv2-act" disabled={gp.picked.count === 0}
                title="Take every category off the selected items"
                onClick={() => onSetCategory(gp.picked.entries.map(e => e.realIndex), null, 'clear')}>
                Clear all
              </button>
            </>
          ) : mode === 'move' ? (
            <>
              {lendingEnabled && onLendItems && (
                <button className="lv2-act" disabled={movable.lend === 0}
                  title="Record these as lent out — they stay in your totals"
                  onClick={() => {
                    onLendItems(gp.picked.entries.filter(e => !e.row.lend).map(e => e.realIndex));
                    pick(null);
                  }}>
                  → Lending{movable.lend ? ` (${movable.lend})` : ''}
                </button>
              )}
              {onMoveToInvestment && (
                <button className="lv2-act primary" disabled={movable.invest === 0}
                  title="Move these to the Vault as long-term holds — they leave the Armory"
                  onClick={() => {
                    onMoveToInvestment(gp.picked.entries.map(e => e.realIndex));
                    pick(null);
                  }}>
                  → Investments{movable.invest ? ` (${movable.invest})` : ''}
                </button>
              )}
            </>
          ) : (
            <button className="lv2-act primary" disabled={gp.picked.count < 2}
              onClick={() => onGroupPrice(gp.picked.entries.map(e => ({ row: e.row, realIndex: e.realIndex })))}>
              Price {gp.picked.count || ''} item{gp.picked.count === 1 ? '' : 's'}
            </button>
          )}
          <button className="lv2-act" onClick={() => pick(null)}>Done</button>
        </div>
      )}

      {detail && (
        <ItemDetailModal
          stickerIndex={stickerIndex}
          variant="classic"
          kind="item"
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          rows={rows || []}
          /* the trade overview draws a thumbnail for every item on both sides */
          skinsData={skinsData}
          /* click-through: any item in the trade chain re-centres this modal */
          onOpenRow={r => setDetail({ row: r, img: getRowImage(r, skinsData, inventoryMap) })}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
